// src/router/index.jsx
import React from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import HomeAlgolia from '../pages/HomeAlgolia';

import Layout from '../layouts/Layout';
import PrivateRoute from '../components/PrivateRoute';
import PrivateAdminRoute from '../components/PrivateAdminRoute';
import AdminLayout from '../layouts/AdminLayout';
import { AuthProvider } from '../context/AuthContext';

// Páginas públicas
import Landing from '../pages/Landing';
import PaddingPage from '../pages/PaddingPage';
import Obrigado from '../pages/Obrigado';
import ConfirmarAssinatura from '../pages/ConfirmarAssinatura';
import CadastroRapido from '../pages/CadastroRapido';
import CadastroUsuario from '../pages/CadastroUsuario';
import Login from '../pages/Login';
import RecuperarSenha from '../pages/RecuperarSenha';
import FinalizarCadastro from '../pages/FinalizarCadastro';
import AssinaturaPendente from '../pages/AssinaturaPendente';

// Microsite — público (engine atual)
import SiteProvider from '../site/SiteContext';
import SiteLayout from '../site/layouts/SiteLayout';
import SiteHomeRouter from '../site/pages/SiteHomeRouter';
import SiteImoveis from '../site/pages/SiteImoveis';
import SiteImovel from '../site/pages/SiteImovel';

// ✅ Lovable — público (template corretor protagonista)
import SiteCorretorLovable from '../site/templates/TemplateCorretorLovable/pages/SiteCorretorLovable';

// Protegidas
import Home from '../pages/home';
import Perfil from '../pages/Perfil';
import EditarPerfil from '../pages/EditarPerfil';
import Cadastrar from '../pages/cadastrar';
import Notificacoes from '../pages/notificacoes';
import Imoveis from '../pages/imoveis';
import About from '../pages/about';
import DetalhesImovel from '../pages/DetalhesImovel';
import BuscarSociosPage from '../pages/BuscarSociosPage';
import SociosPage from '../pages/SociosPage';
import ParceriasPage from '../pages/ParceriasPage';
import Configuracoes from '../pages/Configuracoes';
import DashboardUsuario from '../pages/DashboardUsuario';
import CompararImoveis from '../pages/CompararImoveis';
import EditarImovel from '../components/EditarImovel';
import CadastrarIncorporadora from '../pages/CadastrarIncorporadora';

// ✅ Ponte Imobiliária
import PonteImobiliaria from '../pages/PonteImobiliaria';

// ✅ Diretrizes da Rede
import DiretrizesRede from '../pages/DiretrizesRede/DiretrizesRede';

// 🆕 Central de Negócios & IA
import SalesMachineAI from '../pages/SalesMachineAI';
import MeusNegocios from '../pages/MeusNegocios';
import NegociosComunidade from '../pages/NegociosComunidade';

// 🆕 Página / contexto de Negócio individual (para abrir via notificação)
import NegocioPage from '../pages/NegocioPage';

// Admin
import AdminDashboard from '../pages/admin/DashboardAdmin';
import Assinaturas from '../pages/admin/Assinaturas';
import Pagamentos from '../pages/admin/Pagamentos';
import NotificacoesAdmin from '../pages/admin/Notificacoes';

// Outros
import ErrorPage from '../pages/NotFoundPage';

/* ======================================================
   WRAPPERS (para usar Outlet corretamente)
====================================================== */

function SiteLovableWrapper() {
  return (
    <SiteProvider paramKey="slug">
      <Outlet />
    </SiteProvider>
  );
}

const router = createBrowserRouter([
  /* ======================================================
     🔓 ROTAS PÚBLICAS
  ====================================================== */
  { path: '/landing', element: <Landing />, errorElement: <ErrorPage /> },
  { path: '/padding', element: <PaddingPage />, errorElement: <ErrorPage /> },
  { path: '/obrigado', element: <Obrigado />, errorElement: <ErrorPage /> },
  {
    path: '/confirmar-assinatura',
    element: <ConfirmarAssinatura />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/cadastro-rapido',
    element: <CadastroRapido />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/cadastro-usuario',
    element: <CadastroUsuario />,
    errorElement: <ErrorPage />,
  },
  { path: '/login', element: <Login />, errorElement: <ErrorPage /> },
  {
    path: '/recuperar-senha',
    element: <RecuperarSenha />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/finalizar-cadastro',
    element: (
      <AuthProvider>
        <FinalizarCadastro />
      </AuthProvider>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/assinatura-pendente',
    element: <AssinaturaPendente />,
    errorElement: <ErrorPage />,
  },

  /* ======================================================
     🌐 SITE DO CORRETOR — LOVABLE (PÚBLICO) — /site/:slug/*
  ====================================================== */
  {
    path: '/site/:slug',
    element: <SiteLovableWrapper />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <SiteCorretorLovable /> },
      { path: 'imoveis', element: <SiteCorretorLovable /> },
      { path: 'imovel/:imovelId', element: <SiteCorretorLovable /> },
    ],
  },

  /* ======================================================
     🌐 MICROSITE — PÚBLICO (engine atual) — /u/:handle
  ====================================================== */
  {
    path: '/u/:handle',
    element: (
      <SiteProvider>
        <SiteLayout />
      </SiteProvider>
    ),
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <SiteHomeRouter /> },
      { path: 'imoveis', element: <SiteImoveis /> },
      { path: 'imovel/:imovelId', element: <SiteImovel /> },
    ],
  },

  /* ======================================================
     🔒 ROTAS PROTEGIDAS
  ====================================================== */
  {
    path: '/',
    element: (
      <AuthProvider>
        <PrivateRoute>
          <Outlet />
        </PrivateRoute>
      </AuthProvider>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        element: <Layout />,
        children: [
          { index: true, element: <DashboardUsuario /> },

          { path: 'home', element: <Home /> },
          { path: 'ponte-imobiliaria', element: <PonteImobiliaria /> },

          { path: 'perfil', element: <Perfil /> },
          { path: 'perfil/:id', element: <Perfil /> },

          {
            path: 'editar-perfil',
            element: (
              <SiteProvider>
                <EditarPerfil />
              </SiteProvider>
            ),
          },

          { path: 'cadastrar', element: <Cadastrar /> },
          {
            path: 'cadastrar-incorporadora',
            element: <CadastrarIncorporadora />,
          },

          { path: 'notificacoes', element: <Notificacoes /> },
          { path: 'buscar-socios', element: <BuscarSociosPage /> },

          // Sócios & Parcerias
          { path: 'socios', element: <SociosPage /> },
          { path: 'parcerias', element: <ParceriasPage /> },

          // Imóveis
          { path: 'imoveis', element: <Imoveis /> },
          { path: 'imovel/:id', element: <DetalhesImovel /> },
          { path: 'editar-imovel/:id', element: <EditarImovel /> },
          { path: 'comparar-imoveis', element: <CompararImoveis /> },

          { path: 'about', element: <About /> },
          { path: 'configuracoes', element: <Configuracoes /> },

          // Algolia
          { path: 'explorar-algolia', element: <HomeAlgolia /> },

          /* ==================================================
             ✅ DIRETRIZES DA REDE
             Menu expansível na Sidebar:
             - Manifesto e Cultura
             - Modelo Operacional e Divisão de Ganhos
             - Termos de Uso e Compliance
          ================================================== */
          {
            path: 'diretrizes-rede/*',
            element: <DiretrizesRede />,
          },

          /* ==================================================
             🆕 CENTRAL DE NEGÓCIOS
          ================================================== */
          { path: 'maquina-de-vendas', element: <SalesMachineAI /> },
          { path: 'negocios/meus', element: <MeusNegocios /> },
          { path: 'negocios/comunidade', element: <NegociosComunidade /> },

          // 🔔 rota direta p/ notificação de MATCH
          { path: 'negocios/:negocioId', element: <NegocioPage /> },

          { path: '*', element: <ErrorPage /> },
        ],
      },
    ],
  },

  /* ======================================================
     🔐 ADMIN
  ====================================================== */
  {
    path: '/admin',
    element: (
      <PrivateAdminRoute>
        <AdminLayout />
      </PrivateAdminRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      { path: 'dashboard', element: <AdminDashboard /> },
      { path: 'assinaturas', element: <Assinaturas /> },
      { path: 'pagamentos', element: <Pagamentos /> },
      { path: 'notificacoes', element: <NotificacoesAdmin /> },
      { path: '*', element: <ErrorPage /> },
    ],
  },

  // 404 global
  { path: '*', element: <ErrorPage /> },
]);

export default router;
